import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";

const LAST_UPDATED = "16 April 2025";
const CONTACT_EMAIL = "support@meetmori.app";

export default function Terms() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border/50 bg-background/95 px-5 py-4 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <span className="text-base font-semibold text-foreground">{t("terms.title")}</span>
      </header>

      <main className="mx-auto max-w-prose px-6 py-10">
        <div className="space-y-8 text-sm text-foreground/90 leading-relaxed">

          <div>
            <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            <p className="mt-3">
              These Terms of Service govern your use of the Mori app and website at
              meetmori.app. By creating an account, you agree to these terms. Please
              read them carefully.
            </p>
          </div>

          <Section title="1. The service">
            <p>
              Mori is a family memory-keeping app that helps you record, organise, and
              preserve memories of your children. The service is operated by Dodo
              Solutions s.r.o., registered in the Slovak Republic.
            </p>
          </Section>

          <Section title="2. Accounts">
            <p>
              You must be at least 18 years old to create an account. You are responsible
              for maintaining the security of your account credentials and for all
              activity that occurs under your account.
            </p>
            <p className="mt-2">
              You must provide accurate information when signing up. One account per
              person — creating multiple accounts is not permitted.
            </p>
          </Section>

          <Section title="3. Subscription plans">
            <p>
              Mori offers a free plan (Seedling) and paid plans (Ember, Keeper,
              Storyteller). Paid plans are billed monthly and managed via Stripe.
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                Subscriptions renew automatically at the end of each billing period unless
                cancelled before the renewal date.
              </li>
              <li>
                You may cancel at any time from the Profile page. Access continues until
                the end of the paid period.
              </li>
              <li>
                Refunds are handled on a case-by-case basis. Contact{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> within
                14 days of a charge if you believe a refund is appropriate.
              </li>
              <li>
                We may change pricing with 30 days' notice. Existing subscriptions are
                honoured at the locked-in rate until renewal.
              </li>
            </ul>
          </Section>

          <Section title="4. Your content">
            <p>
              You own the memories, recordings, and photos you add to Mori. By using
              the service, you grant us a limited licence to store, process, and display
              your content solely for the purpose of providing the service to you.
            </p>
            <p className="mt-2">
              You are responsible for the content you upload. You must not upload content
              that is illegal, harmful, or violates the rights of others.
            </p>
            <p className="mt-2">
              We use AI (Google Gemini) to generate written stories from your recordings.
              Your content is not used to train AI models.
            </p>
          </Section>

          <Section title="5. Acceptable use">
            <p>You may not use Mori to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>{t("terms.acceptableUse.violateLaw")}</li>
              <li>{t("terms.acceptableUse.exploitMinors")}</li>
              <li>Attempt to gain unauthorised access to other users' data</li>
              <li>Reverse-engineer, scrape, or copy the service</li>
              <li>{t("terms.acceptableUse.interfere")}</li>
            </ul>
            <p className="mt-2">
              We reserve the right to suspend or terminate accounts that violate these
              rules.
            </p>
          </Section>

          <Section title="6. Family sharing and contributors">
            <p>
              Paid plans allow you to invite family members and contributors via
              invite links. You are responsible for the conduct of anyone you invite to
              your family space. Contributor access is limited to adding memories for
              specific children you share with them.
            </p>
          </Section>

          <Section title="7. Acorns and rewards">
            <p>
              Acorns are in-app credits earned through app activity. They have no
              monetary value, cannot be transferred, and cannot be exchanged for cash.
              We may change, add, or remove acorn rules at any time.
            </p>
          </Section>

          <Section title="8. Availability and changes">
            <p>
              We aim to keep Mori available continuously, but cannot guarantee uptime.
              We may modify, suspend, or discontinue features with reasonable notice.
            </p>
            <p className="mt-2">
              If we discontinue the service entirely, we will give you at least 30 days'
              notice and the opportunity to export your data.
            </p>
          </Section>

          <Section title="9. Limitation of liability">
            <p>
              To the maximum extent permitted by law, Dodo Solutions s.r.o. is not liable
              for any indirect, incidental, or consequential damages arising from your
              use of Mori. Our total liability to you for any claim will not exceed the
              amount you paid us in the 12 months prior to the claim.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These terms are governed by the laws of the Slovak Republic. Disputes will
              be resolved in the courts of the Slovak Republic, unless applicable consumer
              protection laws in your country require otherwise.
            </p>
          </Section>

          <Section title="11. Contact">
            <p>
              Questions about these terms? Email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
            </p>
          </Section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-6 text-center">
        <div className="flex items-center justify-center gap-4">
          <a href="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            meetmori.app
          </a>
          <span className="text-muted-foreground/30 text-xs">·</span>
          <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Privacy policy
          </a>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <div className="text-sm text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}
